import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { User, Save, AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { WorkEmailOverride } from './WorkEmailOverride';
import { useRoles } from '../../hooks/useRoles';
import { api, ApiError } from '../../lib/api';
import type { AdminPersonResponse, PersonApi } from '../../routes/admin/person/types';

export function PersonDetailsPanel({
  person,
  onSaved,
  onEmailChanged,
}: {
  person: PersonApi;
  onSaved: (p: PersonApi) => void;
  /**
   * Called after a work-email override, which appends an event: lets the
   * page refetch the chain so History & Audit shows it without a reload.
   */
  onEmailChanged?: () => void;
}) {
  const [first_name, setFirst] = useState(person.first_name);
  const [last_name, setLast] = useState(person.last_name);
  const [personal_email, setPersonalEmail] = useState(person.personal_email ?? '');
  const [employee_id, setEmp] = useState(person.employee_id ?? '');
  const [roleId, setRoleId] = useState(person.role_id ?? '');
  const [manager, setManager] = useState(person.manager ?? '');
  const [contract, setContract] = useState(person.contract ?? '');
  const [start_date, setStart] = useState(toDateInputValue(person.start_date));
  const [notes, setNotes] = useState(person.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const roles = useRoles();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const r = await api<AdminPersonResponse>(
        `/api/admin/people/${person.id}`,
        {
          method: 'PUT',
          body: JSON.stringify({
            first_name,
            last_name,
            personal_email: personal_email.trim() || null,
            employee_id: employee_id.trim() || null,
            // Only send role_id when it actually changed: re-sending an
            // unchanged value would re-resolve it (rejecting an archived
            // role on an unrelated edit) and clearing it when it was never
            // set would wipe the position mirror.
            ...(roleId !== (person.role_id ?? '')
              ? { role_id: roleId || null }
              : {}),
            manager: manager.trim() || null,
            contract: contract.trim() || null,
            start_date: start_date.trim() || null,
            notes: notes.trim() || null,
          }),
        },
      );
      toast.success(`Saved ${r.person.full_name}`);
      onSaved(r.person);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'save failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm"
    >
      <div className="flex items-center gap-2 border-b border-line/10 px-5 py-3">
        <User className="h-4 w-4 text-fg-muted" />
        <h2 className="text-sm font-semibold">Details</h2>
      </div>
      <div className="p-5 sm:p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="First name" value={first_name} onChange={setFirst} required />
          <Field label="Last name" value={last_name} onChange={setLast} required />
          <WorkEmailOverride
            person={person}
            onChanged={(p) => {
              onSaved(p);
              onEmailChanged?.();
            }}
          />
          <Field label="Personal email" value={personal_email} onChange={setPersonalEmail} />
          <Field label="Employee ID" value={employee_id} onChange={setEmp} />
          <label className="block text-sm">
            <span className="font-medium text-fg-muted">Role</span>
            <select
              value={roleId}
              onChange={(e) => setRoleId(e.target.value)}
              className="mt-1.5 block h-10 w-full rounded-md border border-line-strong/20 bg-surface px-3 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
            >
              <option value="">No role</option>
              {/* Keep the current role selectable even if it's since been archived. */}
              {person.role_id && !(roles.data ?? []).some((r) => r.id === person.role_id) && person.position ? (
                <option value={person.role_id}>{person.position} (archived)</option>
              ) : null}
              {(roles.data ?? []).map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </label>
          <Field label="Manager" value={manager} onChange={setManager} />
          <Field label="Contract" value={contract} onChange={setContract} />
          <Field label="Start date" type="date" value={start_date} onChange={setStart} />
        </div>
        <label className="mt-4 block text-sm">
          <span className="font-medium text-fg-muted">
            Notes <span className="font-normal text-fg-dim">(exceptions, late arrivals, returning-later)</span>
          </span>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-1.5 block w-full rounded-md border border-line-strong/20 bg-surface px-3 py-2 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
          />
        </label>

        {error ? (
          <div className="mt-3 flex items-start gap-2 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}
      </div>
      <div className="flex justify-end border-t border-line/10 bg-surface-2 px-5 py-3">
        <Button
          type="submit"
          variant="primary"
          size="md"
          iconLeft={<Save className="h-4 w-4" />}
          loading={saving}
        >
          Save details
        </Button>
      </div>
    </form>
  );
}

/**
 * Normalise a stored start_date into the YYYY-MM-DD an <input type="date">
 * expects. The DATE column comes back as a timezone-shifted ISO timestamp
 * (e.g. "2026-07-14T23:00:00.000Z" for the 15th in BST), so format from local
 * components rather than slicing the UTC string, which would lose a day.
 */
function toDateInputValue(value: string | null): string {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function Field({
  label,
  value,
  onChange,
  required,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange?: (v: string) => void;
  required?: boolean;
  type?: 'text' | 'date';
}) {
  return (
    <label className="block text-sm">
      <span className="font-medium text-fg-muted">{label}</span>
      <input
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange?.(e.target.value)}
        className="mt-1.5 block h-10 w-full rounded-md border border-line-strong/20 bg-surface px-3 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15 disabled:bg-surface-2 disabled:text-fg-muted"
      />
    </label>
  );
}
